import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listOfShift, deleteShift } from "../../../../Api/HRshiftSetting";
import { toast } from "react-toastify";
import {
  centerIDPayload,
  checkStatusCodeSuccess,
  removeSeconds,
  rendorBackButton,
} from "../../../../Constant/common";
import { shiftSettingLabel } from "../../../../Constant/HR/ShiftSetting";
import { notFound } from "../../../../Constant";
import { holidayLabel } from "../../../../Constant/HR_HolidayTemplate/holidayTemplate";
import { useSelectedCenter } from "../../../CenterContext";
import { usePageLoader } from "../../../../_helper/loading/LoadingContext";
import { Plus, Edit2, Users, Clock, AlertCircle, Trash2 } from "react-feather";
import BaseTooltip from "../../../../BaseComponents/BaseTooltip";
import BaseModal from "../../../../BaseComponents/BaseModal";
import "./ShiftSetting.css";

const parseBreakMinutes = (breakTime) => {
  if (breakTime == null || breakTime === "") return 0;

  if (typeof breakTime === "number") {
    return breakTime > 0 ? breakTime : 0;
  }

  const str = String(breakTime).trim().toLowerCase();
  if (!str || str === "0" || str === "00:00" || str === "00:00:00") return 0;

  // Format "HH:MM:SS" or "HH:MM"
  if (str.includes(":")) {
    const parts = str.split(":").map((p) => Number(p.trim()) || 0);
    if (parts.length === 3) {
      return parts[0] * 60 + parts[1] + Math.round(parts[2] / 60);
    }
    if (parts.length === 2) {
      return parts[0] * 60 + parts[1];
    }
    if (parts.length === 1) {
      return parts[0];
    }
  }

  // Format with text e.g. "1 hr 30 min", "45 min", "45 mins", "45 minute", "45 minutes"
  let totalMins = 0;
  const hrMatch = str.match(/(\d+(?:\.\d+)?)\s*(?:hr|hour|hours|hrs)/);
  const minMatch = str.match(/(\d+)\s*(?:min|minute|minutes|mins|m)/);

  if (hrMatch) {
    totalMins += parseFloat(hrMatch[1]) * 60;
  }
  if (minMatch) {
    totalMins += parseInt(minMatch[1], 10);
  }
  if (hrMatch || minMatch) {
    return Math.round(totalMins);
  }

  // Pure numeric string e.g. "45"
  const numericVal = parseFloat(str);
  if (!isNaN(numericVal) && numericVal > 0) {
    return Math.round(numericVal);
  }

  return 0;
};

const calculateWorkingHours = (inTime, outTime, breakTime) => {
  if (!inTime || !outTime) return "";
  const inParts = String(inTime)
    .split(":")
    .map((p) => parseInt(p, 10));
  const outParts = String(outTime)
    .split(":")
    .map((p) => parseInt(p, 10));
  if (inParts.length < 2 || outParts.length < 2) return "";
  const [inH, inM] = inParts;
  const [outH, outM] = outParts;
  if (isNaN(inH) || isNaN(inM) || isNaN(outH) || isNaN(outM)) return "";

  let inMinutes = inH * 60 + inM;
  let outMinutes = outH * 60 + outM;

  // Handle overnight shift (e.g. 22:00 to 07:00)
  if (outMinutes <= inMinutes) {
    outMinutes += 24 * 60;
  }

  let totalMinutes = outMinutes - inMinutes;

  const breakMinutes = parseBreakMinutes(breakTime);
  if (breakMinutes > 0 && breakMinutes < totalMinutes) {
    totalMinutes -= breakMinutes;
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0 && minutes > 0) {
    return `${hours} hr ${minutes} min`;
  } else if (hours > 0) {
    return `${hours} hr${hours !== 1 ? "s" : ""}`;
  } else if (minutes > 0) {
    return `${minutes} min${minutes !== 1 ? "s" : ""}`;
  }
  return "0 hr";
};

const ShiftSetting = () => {
  const [shiftData, setShiftData] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [shiftToDelete, setShiftToDelete] = useState(null);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const navigate = useNavigate();
  const { selectedCenterPayload } = useSelectedCenter();
  const selectedCenter = selectedCenterPayload.center_id;
  const { setLoading } = usePageLoader();

  useEffect(() => {
    document.title = shiftSettingLabel.ShiftSetting;
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listOfShift(selectedCenter ? `${centerIDPayload}${selectedCenter}` : "");
      if (checkStatusCodeSuccess(res?.statusCode)) setShiftData(res?.data || []);
      else {
        toast.error(res?.message);
        setShiftData([]);
      }
    } catch {
      setShiftData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCenter, setLoading]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDeleteClick = (shift) => {
    setShiftToDelete(shift);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!shiftToDelete?.id) return;
    setDeleteLoader(true);
    try {
      const res = await deleteShift(shiftToDelete.id);
      if (checkStatusCodeSuccess(res?.statusCode)) {
        toast.success(res?.message || "Shift deleted successfully.");
        setShowDeleteModal(false);
        setShiftToDelete(null);
        fetchData();
      } else {
        toast.error(res?.message || "Failed to delete shift.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Something went wrong.");
    } finally {
      setDeleteLoader(false);
    }
  };

  return (
    <div className="animate-fade-in shift-setting-container">
      {/* Header */}
      <div className="animate-slide-down shift-setting-header">
        <div>
          <h1 className="shift-setting-title">{shiftSettingLabel.SHIFTSETTING}</h1>
          <p className="shift-setting-subtitle">
            Define work shifts and assign them to staff members
          </p>
        </div>
        <div className="shift-setting-actions">
          <button onClick={() => navigate("/AddShift")} className="shift-setting-create-btn">
            <Plus size={15} strokeWidth={2.5} /> {shiftSettingLabel.Create}
          </button>
          {rendorBackButton(() => navigate("/setting"))}
        </div>
      </div>

      {/* Shift list */}
      {shiftData?.length > 0 ? (
        <div className="shift-setting-list">
          {shiftData.map((data) => {
            const workingHours = calculateWorkingHours(
              data?.shift_in_time ?? data?.in_time ?? data?.shiftInTime,
              data?.shift_out_time ?? data?.out_time ?? data?.shiftOutTime,
              data?.break_time ??
                data?.breakTime ??
                data?.break_minutes ??
                data?.breakMinutes ??
                data?.break,
            );

            return (
              <div key={data?.id} className="animate-slide-up shift-setting-card">
                <div className="shift-setting-icon-box">
                  <Clock size={19} strokeWidth={2} className="shift-setting-icon" />
                </div>

                <div className="shift-setting-info">
                  <div className="shift-setting-name-row">
                    <span className="shift-setting-name">{data?.shift_name}</span>
                  </div>

                  <div className="shift-setting-meta-row">
                    {/* Time + Duration Unified Capsule */}
                    <div className="shift-setting-time-capsule">
                      <span>
                        {removeSeconds(data?.shift_in_time)} – {removeSeconds(data?.shift_out_time)}
                      </span>
                      {workingHours && (
                        <>
                          <span className="shift-setting-dot" />
                          <span className="shift-setting-duration-badge">{workingHours}</span>
                        </>
                      )}
                    </div>

                    {/* Staff Applied Capsule */}
                    <div className="shift-setting-staff-capsule">
                      <Users size={12} strokeWidth={2} />
                      <span>{data?.totalAssignStaff || 0} Staff</span>
                    </div>
                  </div>
                </div>

                <div className="shift-setting-row-actions">
                  <BaseTooltip label={holidayLabel.editTemplate} color="#b45309">
                    <button
                      onClick={() => navigate(`/EditShift/${data?.id}`)}
                      className="shift-setting-action-btn edit"
                    >
                      <Edit2 size={14} strokeWidth={2} />
                    </button>
                  </BaseTooltip>
                  <BaseTooltip label={holidayLabel.manageStaff} color="#047857">
                    <button
                      onClick={() => navigate(`/manageShiftStaff/${data?.id}`)}
                      className="shift-setting-action-btn staff"
                    >
                      <Users size={14} strokeWidth={2} />
                    </button>
                  </BaseTooltip>
                  <BaseTooltip label="Delete Shift" color="#be123c">
                    <button
                      onClick={() => handleDeleteClick(data)}
                      className="shift-setting-action-btn delete"
                    >
                      <Trash2 size={14} strokeWidth={2} />
                    </button>
                  </BaseTooltip>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="shift-setting-empty">
          <AlertCircle size={40} className="shift-setting-empty-icon" />
          <p className="shift-setting-empty-text">{notFound.dataNotFound}</p>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <BaseModal
        isOpen={showDeleteModal}
        title="Delete Shift"
        submitText="Delete"
        submit={handleConfirmDelete}
        loader={deleteLoader}
        disabled={deleteLoader}
        toggler={() => {
          if (!deleteLoader) {
            setShowDeleteModal(false);
            setShiftToDelete(null);
          }
        }}
      >
        <div className="shift-delete-modal-body">
          <div className="shift-delete-modal-icon-box">
            <Trash2 size={16} strokeWidth={2} className="shift-delete-modal-icon" />
          </div>
          <p className="shift-delete-modal-text">
            {`Are you sure you want to delete the shift "${shiftToDelete?.shift_name || ""}"? This action cannot be undone.`}
          </p>
        </div>
      </BaseModal>
    </div>
  );
};

export default ShiftSetting;
